import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  AIUserProfile,
  SkillAssessment,
  LearningPreferences,
  BehaviorPattern,
  MasteryState,
  AdaptationEvent,
  RecommendationFeedback,
  AIError,
} from '@coffeeplexity/shared';
import type { RootState } from '../index';

interface AIProfileState {
  profile: AIUserProfile | null;
  skillAssessment: SkillAssessment | null;
  learningPreferences: LearningPreferences | null;
  behaviorPatterns: BehaviorPattern[];
  masteryStates: MasteryState[];
  adaptationHistory: AdaptationEvent[];
  recommendationFeedback: RecommendationFeedback[];
  loading: {
    profile: boolean;
    assessment: boolean;
    preferences: boolean;
    mastery: boolean;
    adaptation: boolean;
    feedback: boolean;
  };
  error: AIError | null;
  lastUpdated: string | null;
  cacheExpiry: string | null;
  isOffline: boolean;
  pendingSync: number;
}

const initialState: AIProfileState = {
  profile: null,
  skillAssessment: null,
  learningPreferences: null,
  behaviorPatterns: [],
  masteryStates: [],
  adaptationHistory: [],
  recommendationFeedback: [],
  loading: {
    profile: false,
    assessment: false,
    preferences: false,
    mastery: false,
    adaptation: false,
    feedback: false,
  },
  error: null,
  lastUpdated: null,
  cacheExpiry: null,
  isOffline: false,
  pendingSync: 0,
};

// Async thunks
export const fetchAIProfile = createAsyncThunk(
  'aiProfile/fetchProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/profile/${userId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch AI profile: ${response.statusText}`);
      }
      const profile: AIUserProfile = await response.json();
      return profile;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'PROFILE_FETCH_ERROR',
        message: error.message || 'Failed to fetch AI profile',
        recoverable: true,
        fallbackStrategy: 'generic_experience',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const updateSkillAssessment = createAsyncThunk(
  'aiProfile/updateSkillAssessment',
  async (assessment: Partial<SkillAssessment>, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/assessment', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assessment),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to update skill assessment: ${response.statusText}`
        );
      }
      const updatedAssessment: SkillAssessment = await response.json();
      return updatedAssessment;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ASSESSMENT_UPDATE_ERROR',
        message: error.message || 'Failed to update skill assessment',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const updateLearningPreferences = createAsyncThunk(
  'aiProfile/updateLearningPreferences',
  async (preferences: Partial<LearningPreferences>, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to update learning preferences: ${response.statusText}`
        );
      }
      const updatedPreferences: LearningPreferences = await response.json();
      return updatedPreferences;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'PREFERENCES_UPDATE_ERROR',
        message: error.message || 'Failed to update learning preferences',
        recoverable: true,
        fallbackStrategy: 'generic_experience',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchMasteryStates = createAsyncThunk(
  'aiProfile/fetchMasteryStates',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/mastery/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch mastery states: ${response.statusText}`
        );
      }
      const masteryStates: MasteryState[] = await response.json();
      return masteryStates;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'MASTERY_FETCH_ERROR',
        message: error.message || 'Failed to fetch mastery states',
        recoverable: true,
        fallbackStrategy: 'generic_experience',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const recordAdaptationEvent = createAsyncThunk(
  'aiProfile/recordAdaptationEvent',
  async (
    event: Omit<AdaptationEvent, 'id' | 'timestamp'>,
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/adaptation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to record adaptation event: ${response.statusText}`
        );
      }
      const adaptationEvent: AdaptationEvent = await response.json();
      return adaptationEvent;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ADAPTATION_RECORD_ERROR',
        message: error.message || 'Failed to record adaptation event',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const submitRecommendationFeedback = createAsyncThunk(
  'aiProfile/submitRecommendationFeedback',
  async (
    feedback: Omit<RecommendationFeedback, 'id' | 'timestamp'>,
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedback),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to submit recommendation feedback: ${response.statusText}`
        );
      }
      const recommendationFeedback: RecommendationFeedback =
        await response.json();
      return recommendationFeedback;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'FEEDBACK_SUBMIT_ERROR',
        message: error.message || 'Failed to submit recommendation feedback',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

const aiProfileSlice = createSlice({
  name: 'aiProfile',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearProfile: state => {
      state.profile = null;
      state.skillAssessment = null;
      state.learningPreferences = null;
      state.behaviorPatterns = [];
      state.masteryStates = [];
      state.adaptationHistory = [];
      state.recommendationFeedback = [];
      state.lastUpdated = null;
      state.cacheExpiry = null;
    },
    updateBehaviorPattern: (state, action: PayloadAction<BehaviorPattern>) => {
      const existingIndex = state.behaviorPatterns.findIndex(
        pattern => pattern.id === action.payload.id
      );
      if (existingIndex >= 0) {
        state.behaviorPatterns[existingIndex] = action.payload;
      } else {
        state.behaviorPatterns.push(action.payload);
      }
    },
    updateMasteryState: (state, action: PayloadAction<MasteryState>) => {
      const existingIndex = state.masteryStates.findIndex(
        mastery => mastery.skillId === action.payload.skillId
      );
      if (existingIndex >= 0) {
        state.masteryStates[existingIndex] = action.payload;
      } else {
        state.masteryStates.push(action.payload);
      }
    },
    setOfflineStatus: (
      state,
      action: PayloadAction<{ isOffline: boolean; pendingSync: number }>
    ) => {
      state.isOffline = action.payload.isOffline;
      state.pendingSync = action.payload.pendingSync;
    },
    setCacheExpiry: (state, action: PayloadAction<string>) => {
      state.cacheExpiry = action.payload;
    },
    optimisticUpdatePreferences: (
      state,
      action: PayloadAction<Partial<LearningPreferences>>
    ) => {
      if (state.learningPreferences) {
        state.learningPreferences = {
          ...state.learningPreferences,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: builder => {
    // Fetch AI Profile
    builder
      .addCase(fetchAIProfile.pending, state => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(fetchAIProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.profile = action.payload;
        state.skillAssessment = action.payload.skillAssessment;
        state.learningPreferences = action.payload.learningPreferences;
        state.behaviorPatterns = action.payload.behaviorPatterns;
        state.masteryStates = action.payload.masteryStates;
        state.adaptationHistory = action.payload.adaptationHistory;
        state.recommendationFeedback = action.payload.recommendationFeedback;
        state.lastUpdated = action.payload.lastUpdated;
        state.cacheExpiry = new Date(Date.now() + 30 * 60 * 1000).toISOString(); // 30 minutes
      })
      .addCase(fetchAIProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload as AIError;
      });

    // Update Skill Assessment
    builder
      .addCase(updateSkillAssessment.pending, state => {
        state.loading.assessment = true;
        state.error = null;
      })
      .addCase(updateSkillAssessment.fulfilled, (state, action) => {
        state.loading.assessment = false;
        state.skillAssessment = action.payload;
        if (state.profile) {
          state.profile.skillAssessment = action.payload;
          state.profile.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(updateSkillAssessment.rejected, (state, action) => {
        state.loading.assessment = false;
        state.error = action.payload as AIError;
      });

    // Update Learning Preferences
    builder
      .addCase(updateLearningPreferences.pending, state => {
        state.loading.preferences = true;
        state.error = null;
      })
      .addCase(updateLearningPreferences.fulfilled, (state, action) => {
        state.loading.preferences = false;
        state.learningPreferences = action.payload;
        if (state.profile) {
          state.profile.learningPreferences = action.payload;
          state.profile.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(updateLearningPreferences.rejected, (state, action) => {
        state.loading.preferences = false;
        state.error = action.payload as AIError;
      });

    // Fetch Mastery States
    builder
      .addCase(fetchMasteryStates.pending, state => {
        state.loading.mastery = true;
        state.error = null;
      })
      .addCase(fetchMasteryStates.fulfilled, (state, action) => {
        state.loading.mastery = false;
        state.masteryStates = action.payload;
        if (state.profile) {
          state.profile.masteryStates = action.payload;
          state.profile.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(fetchMasteryStates.rejected, (state, action) => {
        state.loading.mastery = false;
        state.error = action.payload as AIError;
      });

    // Record Adaptation Event
    builder
      .addCase(recordAdaptationEvent.pending, state => {
        state.loading.adaptation = true;
        state.error = null;
      })
      .addCase(recordAdaptationEvent.fulfilled, (state, action) => {
        state.loading.adaptation = false;
        state.adaptationHistory.unshift(action.payload);
        if (state.profile) {
          state.profile.adaptationHistory.unshift(action.payload);
          state.profile.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(recordAdaptationEvent.rejected, (state, action) => {
        state.loading.adaptation = false;
        state.error = action.payload as AIError;
      });

    // Submit Recommendation Feedback
    builder
      .addCase(submitRecommendationFeedback.pending, state => {
        state.loading.feedback = true;
        state.error = null;
      })
      .addCase(submitRecommendationFeedback.fulfilled, (state, action) => {
        state.loading.feedback = false;
        state.recommendationFeedback.unshift(action.payload);
        if (state.profile) {
          state.profile.recommendationFeedback.unshift(action.payload);
          state.profile.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(submitRecommendationFeedback.rejected, (state, action) => {
        state.loading.feedback = false;
        state.error = action.payload as AIError;
      });
  },
});

export const {
  clearError,
  clearProfile,
  updateBehaviorPattern,
  updateMasteryState,
  setOfflineStatus,
  setCacheExpiry,
  optimisticUpdatePreferences,
} = aiProfileSlice.actions;

// Memoized selectors for performance
export const selectAIProfile = (state: RootState) => state.aiProfile.profile;
export const selectSkillAssessment = (state: RootState) =>
  state.aiProfile.skillAssessment;
export const selectLearningPreferences = (state: RootState) =>
  state.aiProfile.learningPreferences;
export const selectMasteryStates = (state: RootState) =>
  state.aiProfile.masteryStates;
export const selectBehaviorPatterns = (state: RootState) =>
  state.aiProfile.behaviorPatterns;
export const selectAdaptationHistory = (state: RootState) =>
  state.aiProfile.adaptationHistory;
export const selectRecommendationFeedback = (state: RootState) =>
  state.aiProfile.recommendationFeedback;
export const selectAIProfileLoading = (state: RootState) =>
  state.aiProfile.loading;
export const selectAIProfileError = (state: RootState) => state.aiProfile.error;
export const selectIsOffline = (state: RootState) => state.aiProfile.isOffline;
export const selectPendingSync = (state: RootState) =>
  state.aiProfile.pendingSync;

// Complex memoized selectors
export const selectMasteryByCategory = createSelector(
  [selectMasteryStates],
  masteryStates => {
    return masteryStates.reduce(
      (acc, mastery) => {
        if (!acc[mastery.skillCategory]) {
          acc[mastery.skillCategory] = [];
        }
        if (!acc[mastery.skillCategory]) {
          acc[mastery.skillCategory] = [];
        }
        acc[mastery.skillCategory].push(mastery);
        return acc;
      },
      {} as Record<string, MasteryState[]>
    );
  }
);

export const selectOverallMasteryLevel = createSelector(
  [selectMasteryStates],
  masteryStates => {
    if (masteryStates.length === 0) return 0;
    const totalMastery = masteryStates.reduce(
      (sum, mastery) => sum + mastery.masteryLevel,
      0
    );
    return totalMastery / masteryStates.length;
  }
);

export const selectRecentAdaptations = createSelector(
  [selectAdaptationHistory],
  adaptationHistory => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return adaptationHistory.filter(
      adaptation => new Date(adaptation.timestamp) > oneWeekAgo
    );
  }
);

export const selectEngagementPatterns = createSelector(
  [selectBehaviorPatterns],
  behaviorPatterns => {
    return behaviorPatterns.filter(
      pattern => pattern.patternType === 'engagement'
    );
  }
);

export const selectLearningPatterns = createSelector(
  [selectBehaviorPatterns],
  behaviorPatterns => {
    return behaviorPatterns.filter(
      pattern => pattern.patternType === 'learning'
    );
  }
);

export const selectIsProfileStale = createSelector(
  [selectAIProfile, (state: RootState) => state.aiProfile.cacheExpiry],
  (profile, cacheExpiry) => {
    if (!profile || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export default aiProfileSlice.reducer;
